// hooks/useNotifications.ts
//
// Real-time notification hook.
//
// Primary transport: WebSocket via Django Channels (/ws/notifications/?token=JWT)
// Fallback: HTTP polling every 5s (active) / 30s (idle) if WS is unavailable.
//
// The hook's public interface is unchanged — consumers (NotificationBell,
// TopNavBarApp) see the same return type regardless of transport.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { fetchData, createData, updateData } from "api/apiHelpers";
import logger from "utils/logger";
import { RootState } from "../types";

export interface TaskNotification {
  id: number;
  task_type: "document_upload" | "llm_extraction" | "bulk_upload" | "bulk_extraction" | "export" | "environment_invite" | "payment_failed";
  status: "pending" | "in_progress" | "completed" | "failed";
  title: string;
  detail: string;
  patient_id: number | null;
  patient_name: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  read_at: string | null;
}

interface NotificationsResponse {
  unread_count: number;
  results: TaskNotification[];
}

// ─── Consolidation ───────────────────────────────────────────────────────

function consolidateNotifications(raw: TaskNotification[]): TaskNotification[] {
  const bulkNotifs: TaskNotification[] = [];
  const bulkJobPatientIds = new Set<number>();
  const extractionByPatient = new Map<number, TaskNotification>();
  const other: TaskNotification[] = [];

  // First pass: identify bulk_extraction notifications and collect
  // the patient IDs that belong to bulk jobs (from created_patient_ids in metadata).
  for (const n of raw) {
    if (n.task_type === "bulk_extraction" || n.task_type === "bulk_upload") {
      bulkNotifs.push(n);
      const meta = n.metadata as Record<string, unknown>;
      const ids = meta?.created_patient_ids;
      if (Array.isArray(ids)) {
        for (const id of ids) {
          if (typeof id === "number") bulkJobPatientIds.add(id);
        }
      }
    }
  }

  // Second pass: filter out per-document/extraction noise for bulk patients.
  for (const n of raw) {
    if (n.task_type === "bulk_extraction" || n.task_type === "bulk_upload") {
      continue; // already collected
    }

    // Suppress per-document notifications that belong to a bulk job
    if (
      n.patient_id &&
      bulkNotifs.length > 0 &&
      (n.task_type === "document_upload" || n.task_type === "llm_extraction")
    ) {
      // If we know the patient IDs, filter precisely; otherwise suppress
      // all per-doc notifications when any bulk job is present.
      if (bulkJobPatientIds.size > 0) {
        if (bulkJobPatientIds.has(n.patient_id)) continue;
      }
    }

    if (
      n.patient_id &&
      (n.task_type === "llm_extraction" || n.task_type === "document_upload")
    ) {
      const existing = extractionByPatient.get(n.patient_id);
      if (!existing) {
        extractionByPatient.set(n.patient_id, n);
      } else {
        const preferNew =
          n.task_type === "llm_extraction" && existing.task_type !== "llm_extraction"
            ? true
            : n.task_type === existing.task_type &&
              new Date(n.updated_at) > new Date(existing.updated_at);
        if (preferNew) {
          extractionByPatient.set(n.patient_id, n);
        }
      }
    } else {
      other.push(n);
    }
  }

  const consolidated = [
    ...bulkNotifs,
    ...other,
    ...extractionByPatient.values(),
  ];
  consolidated.sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );
  return consolidated;
}

// ─── Constants ───────────────────────────────────────────────────────────

const ACTIVE_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;
const WS_RECONNECT_BASE_MS = 1_000;
const WS_RECONNECT_MAX_MS = 30_000;
const WS_PING_INTERVAL_MS = 25_000;

export type NotificationChangeCallback = (n: TaskNotification) => void;

// ─── WebSocket URL helper ────────────────────────────────────────────────

function buildWsUrl(token: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws/notifications/?token=${encodeURIComponent(token)}`;
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useNotifications(
  onNewNotification?: NotificationChangeCallback,
) {
  const token = useSelector((state: RootState) => state.auth.token);
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  // Refs that survive re-renders
  const seenRef = useRef<Map<number, string>>(new Map());
  const isFirstFetchRef = useRef(true);
  const onNewNotifRef = useRef(onNewNotification);
  onNewNotifRef.current = onNewNotification;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempt = useRef(0);
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const rawNotificationsRef = useRef<TaskNotification[]>([]);

  const hasActiveTasks = notifications.some(
    (n) => n.status === "pending" || n.status === "in_progress"
  );

  // ── Change detection & toast ────────────────────────────────────────

  const detectChangesAndToast = useCallback((incoming: TaskNotification[]) => {
    if (!isFirstFetchRef.current && onNewNotifRef.current) {
      for (const n of incoming) {
        const key = `${n.status}::${n.detail}`;
        const prev = seenRef.current.get(n.id);
        if (prev !== key) {
          if (!prev || n.status === "completed" || n.status === "failed") {
            onNewNotifRef.current(n);
          }
        }
      }
    }
    isFirstFetchRef.current = false;

    const newSeen = new Map<number, string>();
    for (const n of incoming) {
      newSeen.set(n.id, `${n.status}::${n.detail}`);
    }
    seenRef.current = newSeen;
  }, []);

  // ── REST fetch (initial load + polling fallback) ────────────────────

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await fetchData<NotificationsResponse>(
        "/billing/notifications/"
      );
      const raw = data.results;
      detectChangesAndToast(raw);
      rawNotificationsRef.current = raw;
      setNotifications(consolidateNotifications(raw));
      setUnreadCount(data.unread_count);
    } catch (err) {
      logger.warn("Failed to fetch notifications", err);
    }
  }, [detectChangesAndToast]);

  // ── Apply a single pushed notification into local state ─────────────

  const applyPushedNotification = useCallback((pushed: TaskNotification) => {
    const raw = rawNotificationsRef.current;
    const idx = raw.findIndex((n) => n.id === pushed.id);
    let updated: TaskNotification[];
    if (idx >= 0) {
      updated = [...raw];
      updated[idx] = pushed;
    } else {
      updated = [pushed, ...raw];
    }
    rawNotificationsRef.current = updated;

    detectChangesAndToast(updated);
    setNotifications(consolidateNotifications(updated));

    const newUnread = updated.filter((n) => !n.read_at).length;
    setUnreadCount(newUnread);
  }, [detectChangesAndToast]);

  // ── WebSocket lifecycle ─────────────────────────────────────────────

  const cleanupWs = useCallback(() => {
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      if (wsRef.current.readyState <= WebSocket.OPEN) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
    setWsConnected(false);
  }, []);

  const connectWs = useCallback(() => {
    if (!token) return;
    cleanupWs();

    try {
      const ws = new WebSocket(buildWsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempt.current = 0;
        setWsConnected(true);
        logger.info("[WS] Notification WebSocket connected");

        // Keep-alive ping every 25s to prevent proxy timeout (nginx 300s)
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, WS_PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "notification" && msg.notification) {
            applyPushedNotification(msg.notification as TaskNotification);
          }
          // "pong" messages are silently ignored
        } catch {
          logger.warn("[WS] Failed to parse message", event.data);
        }
      };

      ws.onclose = (event) => {
        setWsConnected(false);
        if (pingTimer.current) {
          clearInterval(pingTimer.current);
          pingTimer.current = null;
        }

        // 4001 = auth rejected — don't reconnect
        if (event.code === 4001) {
          logger.warn("[WS] Auth rejected, not reconnecting");
          return;
        }

        const delay = Math.min(
          WS_RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt.current),
          WS_RECONNECT_MAX_MS,
        );
        reconnectAttempt.current += 1;
        logger.info(`[WS] Disconnected (code=${event.code}), reconnecting in ${delay}ms`);
        reconnectTimer.current = setTimeout(connectWs, delay);
      };

      ws.onerror = () => {
        logger.warn("[WS] WebSocket error");
      };
    } catch (err) {
      logger.warn("[WS] Failed to create WebSocket", err);
    }
  }, [token, cleanupWs, applyPushedNotification]);

  // ── Polling (fallback when WS is not connected) ─────────────────────

  useEffect(() => {
    // Always do an initial REST fetch to get the full list
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    // When WS is connected, stop polling.
    // When WS is not connected, poll at adaptive intervals.
    if (wsConnected) {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }

    const interval = hasActiveTasks ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    pollTimer.current = setInterval(fetchNotifications, interval);

    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [wsConnected, hasActiveTasks, fetchNotifications]);

  // ── Connect / disconnect WS on mount / unmount / token change ───────

  useEffect(() => {
    if (token) {
      connectWs();
    } else {
      cleanupWs();
    }

    return () => {
      cleanupWs();
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };
  }, [token, connectWs, cleanupWs]);

  // ── User actions (REST — unchanged) ─────────────────────────────────

  const markAsRead = useCallback(async (id: number) => {
    try {
      await updateData(`/billing/notifications/${id}/read/`, {});
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      logger.warn("Failed to mark notification as read", err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await createData("/billing/notifications/mark-all-read/", {});
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      logger.warn("Failed to mark all notifications as read", err);
    }
  }, []);

  const clearAll = useCallback(async () => {
    try {
      await createData("/billing/notifications/clear-all/", {});
      setNotifications((prev) => prev.filter((n) => !n.read_at));
    } catch (err) {
      logger.warn("Failed to clear notifications", err);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAll,
    hasActiveTasks,
    wsConnected,
  };
}

export default useNotifications;
